import queryClient from '@src/app/providers/queryClient';
import type { AppQueryKey } from '@src/shared/query/utils';

export const CLIENTS_KEY: AppQueryKey = ['root', 'clients'];

/** The client page; its address lists are keyed below it. */
export function clientDetailKey(id: number | string): AppQueryKey {
  return [...CLIENTS_KEY, 'detail', String(id)];
}

/** The client contact page. */
export function clientContactDetailKey(id: number | string): AppQueryKey {
  return [...CLIENTS_KEY, 'contacts', 'detail', String(id)];
}

/** The client group page. */
export function clientGroupDetailKey(id: number | string): AppQueryKey {
  return [...CLIENTS_KEY, 'groups', 'detail', String(id)];
}

// Lookups that change only in the admin: kept by a save of the module.
const LOOKUP_KEYS: readonly (readonly string[])[] = [
  ['options'],
  ['business-segments'],
  ['groups', 'competitors'],
];

function isLookup(queryKey: readonly unknown[]) {
  const rest = queryKey.slice(CLIENTS_KEY.length);
  return LOOKUP_KEYS.some(
    (key) =>
      key.length === rest.length &&
      key.every((part, index) => part === rest[index]),
  );
}

/** After a save: every query of the module but its lookups. */
export function invalidateClients() {
  return queryClient.invalidateQueries({
    queryKey: CLIENTS_KEY,
    predicate: (query) => !isLookup(query.queryKey),
  });
}

/** Reloads the client page's own record, e.g. before a section is edited. */
export function refreshClientDetail(id: number | string) {
  return queryClient.invalidateQueries({
    queryKey: clientDetailKey(id),
    exact: true,
  });
}

/** Reloads the client group page's record, e.g. before a section is edited. */
export function refreshClientGroupDetail(id: number | string) {
  return queryClient.invalidateQueries({
    queryKey: clientGroupDetailKey(id),
    exact: true,
  });
}
